/**
 * Utilidad para depuración condicional basada en entorno
 * Permite crear depuradores específicos para diferentes módulos
 */
import logger from './logger';

/**
 * Interfaz para las opciones de configuración del depurador
 */
export interface OpcionesDepurador {
  readonly modulo: string;
  readonly habilitado?: boolean;
}

/**
 * Interfaz para el depurador
 */
export interface Depurador {
  readonly info: (mensaje: string, ...datos: readonly unknown[]) => void;
  readonly warn: (mensaje: string, ...datos: readonly unknown[]) => void;
  readonly error: (mensaje: string, ...datos: readonly unknown[]) => void;
  readonly debug: (mensaje: string, ...datos: readonly unknown[]) => void;
  readonly grupo: (etiqueta: string) => void;
  readonly finGrupo: () => void;
}

/**
 * Crea un depurador para un módulo específico
 * 
 * @param opciones - Opciones de configuración del depurador
 * @returns Instancia del depurador
 */
export const crearDepurador = (opciones: OpcionesDepurador): Depurador => {
  const { modulo, habilitado = true } = opciones;
  const prefijo = `[${modulo}]`;

  // Si el depurador no está habilitado, devolver una implementación que no hace nada
  if (!habilitado) {
    return {
      info: () => {},
      warn: () => {},
      error: () => {},
      debug: () => {},
      grupo: () => {},
      finGrupo: () => {},
    };
  }

  return {
    info: (mensaje: string, ...datos: readonly unknown[]): void => {
      logger.info(`${prefijo} ${mensaje}`, ...datos);
    },
    warn: (mensaje: string, ...datos: readonly unknown[]): void => {
      logger.warn(`${prefijo} ${mensaje}`, ...datos);
    },
    error: (mensaje: string, ...datos: readonly unknown[]): void => {
      logger.error(`${prefijo} ${mensaje}`, ...datos);
    },
    debug: (mensaje: string, ...datos: readonly unknown[]): void => {
      logger.debug(`${prefijo} ${mensaje}`, ...datos);
    },
    grupo: (etiqueta: string): void => {
      if (typeof console.group === 'function') {
        console.group(`${prefijo} ${etiqueta}`);
      } else {
        logger.info(`${prefijo} GRUPO: ${etiqueta}`);
      }
    },
    finGrupo: (): void => {
      if (typeof console.groupEnd === 'function') {
        console.groupEnd();
      } else {
        logger.info(`${prefijo} FIN GRUPO`);
      }
    },
  };
};
